import { Icons } from '@/components/ui/icons'
import { cn } from '@/lib/utils'

export interface ProgressStage {
  key?: string | null
  label?: string | null
  status: 'done' | 'current' | 'upcoming'
}

const STATUS_TEXT: Record<ProgressStage['status'], string> = {
  done: 'done',
  current: 'current step',
  upcoming: 'not started',
}

/**
 * Where a record is across its stages, restored from Driver-Auto-CC
 * (`2a4551d^:.../approver/StageProgress.tsx`, plan 08 WP-VIS-06). Each stage
 * has a glyph and a text label, and the current one carries
 * `aria-current="step"`, so state never depends on colour alone (Rule 14).
 */
export function StageProgress({ stages, label }: { stages: ProgressStage[]; label: string }) {
  return (
    <ol aria-label={label} className='flex flex-wrap gap-x-5 gap-y-2'>
      {stages.map((stage, index) => (
        <li
          key={stage.key ?? index}
          aria-current={stage.status === 'current' ? 'step' : undefined}
          className={cn(
            'flex items-center gap-1.5 text-sm',
            stage.status === 'upcoming' ? 'text-muted-foreground' : 'text-foreground',
            stage.status === 'current' && 'font-semibold'
          )}
        >
          {stage.status === 'done' && <Icons.checkCircle className='h-4 w-4 text-success-strong' aria-hidden='true' />}
          {stage.status === 'current' && (
            <span aria-hidden='true' className='h-3 w-3 rounded-full border-2 border-brand-navy bg-brand-cornflower' />
          )}
          {stage.status === 'upcoming' && <Icons.circle className='h-4 w-4' aria-hidden='true' />}
          {stage.label}
          <span className='sr-only'>({STATUS_TEXT[stage.status]})</span>
        </li>
      ))}
    </ol>
  )
}
